import React, { memo, forwardRef, ReactElement } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { useWindowSize } from 'react-use';
import { areEqual, FixedSizeGrid } from 'react-window';
import WindStationCard from 'components/WindStationCard/WindStationCard';
import { NearbyStation } from 'types/windStations';
import { Speed } from 'types/units';
import config from '../../config';

export const DESKTOP_HORIZ_GUTTER_SIZE = 20;
export const MOBILE_HORIZ_GUTTER_SIZE = 8;
export const DESKTOP_VERT_GUTTER_SIZE = 20;
export const MOBILE_VERT_GUTTER_SIZE = 5;

// WARNING: If you change any of these you will need to also change the scss.
// We aren't using javascript because we want to ensure the server-side render
// is able to render the cards in the correct width (SSR doesn't have access to
// device width in JS)
const SMALL_WIND_STATION_CARD_HEIGHT = 130;
const SMALL_WIND_STATION_CARD_WIDTH = 255;
const LARGE_WIND_STATION_CARD_HEIGHT = 127;
const LARGE_WIND_STATION_CARD_WIDTH = 300;

const useGutterSizes = () => {
  const { width } = useWindowSize();

  const isDesktop = width >= config.tabletLargeWidth;

  const horizontalGutterSize = isDesktop ? DESKTOP_HORIZ_GUTTER_SIZE : MOBILE_HORIZ_GUTTER_SIZE;
  const verticalGutterSize = isDesktop ? DESKTOP_VERT_GUTTER_SIZE : MOBILE_VERT_GUTTER_SIZE;

  return { horizontalGutterSize, verticalGutterSize };
};

interface ItemData {
  windStations: NearbyStation[] | undefined;
  windSpeedUnits: string;
  showPaywall: boolean;
  isVerticalView: boolean;
  segmentProperties: {
    pageName: string;
  };
}

interface GridWindStationItemProps {
  columnIndex: number;
  rowIndex: number;
  style: React.CSSProperties;
  data: ItemData;
}

export const GridWindStationItem = memo(
  ({ columnIndex, rowIndex, style, data }: GridWindStationItemProps) => {
    const index = rowIndex * 2 + columnIndex;
    const windStations = data.windStations ? data.windStations : [];
    const windStation = windStations[index];

    const { horizontalGutterSize, verticalGutterSize } = useGutterSizes();

    if (!windStation) {
      return null;
    }

    return (
      <li
        className="sl-wind-station-virtual-list-item"
        style={{
          ...style,
          top: Number(style.top) + verticalGutterSize,
          left: Number(style.left) + horizontalGutterSize,
          width: Number(style.width) - horizontalGutterSize,
          height: Number(style.height) - verticalGutterSize,
        }}
      >
        <WindStationCard
          windStation={windStation}
          windSpeedUnit={data.windSpeedUnits}
          isLoading={false}
          showPaywall={data.showPaywall}
          isVerticalView={data.isVerticalView}
          segmentProperties={data.segmentProperties}
        />
      </li>
    );
  },
  areEqual,
);

GridWindStationItem.displayName = 'GridWindStationItem';

interface GridInnerElementProps {
  style: React.CSSProperties;
  rest: {
    children: ReactElement[];
  };
}

export const GridInnerElement: React.FC<GridInnerElementProps> = memo(
  forwardRef(({ style, ...rest }, ref: React.ForwardedRef<HTMLUListElement>) => {
    const { verticalGutterSize, horizontalGutterSize } = useGutterSizes();
    return (
      <ul
        ref={ref}
        style={{
          ...style,
          listStyleType: 'none',
          paddingLeft: horizontalGutterSize,
          paddingTop: verticalGutterSize,
        }}
        {...rest}
      />
    );
  }),
  areEqual,
);

interface Props {
  isVerticalView: boolean;
  windStations: NearbyStation[] | undefined;
  windSpeedUnits: Speed;
  showPaywall?: boolean;
  segmentProperties: {
    pageName: string;
  };
}

const WindStationsVirtualList: React.FC<Props> = ({
  isVerticalView,
  windStations,
  windSpeedUnits,
  showPaywall = false,
  segmentProperties,
}: Props) => (
  <AutoSizer className="sl-wind-station-virtual-list-autosizer">
    {({ height, width }: any) => {
      const desktopColumnCount = Math.floor(Number(width) / LARGE_WIND_STATION_CARD_WIDTH);

      const rowHeight = isVerticalView
        ? LARGE_WIND_STATION_CARD_HEIGHT + DESKTOP_VERT_GUTTER_SIZE
        : SMALL_WIND_STATION_CARD_HEIGHT + MOBILE_VERT_GUTTER_SIZE;
      const columnWidth = isVerticalView
        ? LARGE_WIND_STATION_CARD_WIDTH + DESKTOP_HORIZ_GUTTER_SIZE
        : SMALL_WIND_STATION_CARD_WIDTH + MOBILE_HORIZ_GUTTER_SIZE;

      const columnCount = isVerticalView ? desktopColumnCount : Number(windStations?.length);
      const rowCount = isVerticalView ? Math.round(Number(windStations?.length) / 2) : 1;

      const itemData: ItemData = {
        windStations,
        windSpeedUnits,
        showPaywall,
        isVerticalView,
        segmentProperties,
      };

      return (
        <FixedSizeGrid
          className="sl-wind-station-virtual-list-grid"
          height={Number(height)}
          width={Number(width)}
          columnWidth={columnWidth}
          columnCount={columnCount}
          rowHeight={rowHeight}
          rowCount={rowCount}
          style={{ overflow: isVerticalView ? 'hidden auto' : 'auto hidden' }}
          itemData={itemData}
          innerElementType={GridInnerElement}
        >
          {GridWindStationItem}
        </FixedSizeGrid>
      );
    }}
  </AutoSizer>
);

export default memo(WindStationsVirtualList);
